import { Directive, ElementRef, Input, OnChanges, SecurityContext, SimpleChanges, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
interface HighlightMatch {
  field: string;
  value: string;
}

interface HighlightData {
  matches?: HighlightMatch[];
}

/**
 * Directive to highlight text based on search matches from elastic search results.
 * Similar to the highlighting functionality in dx-data-grid component.
 * 
 * Usage:
 * <div [highlightText]="searchTerm" [highlightField]="'field_name'" [highlightData]="highlightObject">
 *   Text to be highlighted
 * </div>
 * 
 * For formatted text: add [highlightTextFormatted]="formattedValue" where highlightText is the unformatted raw value.
 */
@Directive({
    selector: '[highlightText]',
    standalone: true
})
export class HighlightTextDirective implements OnChanges {

    /** The field name to match against highlight data */
    @Input() highlightField: string = '';

    /** The highlight data containing matches */
    @Input() highlightData?: HighlightData;

    /** The original unformatted text content (for matching) */
    @Input() highlightText: string = '';

    /** The formatted text content (for display, optional) */
    @Input() highlightTextFormatted?: string;

    private elementRef = inject(ElementRef);
    private sanitizer = inject(DomSanitizer);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['highlightField'] || changes['highlightData'] || changes['highlightText'] || changes['highlightTextFormatted']) {
            this.applyHighlight();
        }
    }

    /**
     * Applies highlighting to the element's content based on the highlight data.
     */
    private applyHighlight(): void {
        const element = this.elementRef.nativeElement as HTMLElement;

        const unformatted = this.highlightText || element.textContent?.replace(/<[^>]*>/g, '') || '';
        let displayText = this.highlightTextFormatted || unformatted;

        if (!displayText || !this.highlightData || !this.highlightField) {
            element.textContent = displayText;
            return;
        }

        const fieldMatches = this.highlightData.matches?.filter(match => match.field === this.highlightField);

        if (!fieldMatches || fieldMatches.length === 0) {
            element.textContent = displayText;
            return;
        }

        let highlightedText: string;

        if (this.highlightTextFormatted && this.highlightText) {
            highlightedText = this.mappedHighlight(unformatted, displayText, fieldMatches);
        } else {
            highlightedText = this.simpleHighlight(displayText, fieldMatches);
        }

        const sanitizedHtml = this.sanitizer.sanitize(SecurityContext.HTML, highlightedText);
        element.innerHTML = sanitizedHtml || displayText;
    }

    /**
     * Simple highlight replace (for non-formatted or fallback).
     */
    private simpleHighlight(text: string, matches: any[]): string {
        const highlights: { start: number; end: number; term: string }[] = [];

        matches.forEach(match => {
            const term = match.value?.trim();
            if (term) {
                const escapedTerm = this.escapeRegExp(term);
                const regex = new RegExp(escapedTerm, 'gi');
                const matchResults = this.findAllMatches(regex, text);
                
                matchResults.forEach(matchResult => {
                    highlights.push({
                        start: matchResult.index,
                        end: matchResult.index + matchResult.length,
                        term: matchResult.match
                    });
                });
            }
        });

        highlights.sort((a, b) => a.start - b.start);
        const merged = this.mergeOverlappingRanges(highlights, text);

        let result = text;
        for (let i = merged.length - 1; i >= 0; i--) {
            const highlight = merged[i];
            const before = result.slice(0, highlight.start);
            const highlighted = result.slice(highlight.start, highlight.end);
            const after = result.slice(highlight.end);
            result = before + `<span class="dx-datagrid-search-text">${highlighted}</span>` + after;
        }

        return result;
    }

    /**
     * Mapped highlight for formatted text.
     */
    private mappedHighlight(unformatted: string, formatted: string, matches: any[]): string {
        const terms = matches
            .map((m: any) => m.value?.trim())
            .filter((term: string) => !!term)
            .map((term: string) => this.escapeRegExp(term))
            .filter((term: string, index: number, self: string[]) => self.indexOf(term) === index);

        const ranges: { start: number; end: number }[] = [];
        terms.forEach(escapedTerm => {
            const regex = new RegExp(escapedTerm, 'gi');
            const matchResults = this.findAllMatches(regex, unformatted);
            
            matchResults.forEach(matchResult => {
                ranges.push({ start: matchResult.index, end: matchResult.index + matchResult.length });
            });
        });

        ranges.sort((a, b) => a.start - b.start);
        const merged = this.mergeOverlappingRanges(ranges);

        let result = '';
        let lastEnd = 0;
        for (const r of merged) {
            const plainStart = this.getMappedPosition(lastEnd, unformatted, formatted);
            const hlStart = this.getMappedPosition(r.start, unformatted, formatted);
            const hlEnd = this.getMappedPosition(r.end, unformatted, formatted);

            result += formatted.slice(plainStart, hlStart);
            const hlText = formatted.slice(hlStart, hlEnd);
            result += `<span class="dx-datagrid-search-text">${hlText}</span>`;
            lastEnd = r.end;
        }
        const finalPlainStart = this.getMappedPosition(lastEnd, unformatted, formatted);
        result += formatted.slice(finalPlainStart);
        return result;
    }

    /**
     * Maps index from unformatted to position in formatted.
     */
    private getMappedPosition(index: number, unformatted: string, formatted: string): number {
        if (index >= unformatted.length) {
            return formatted.length;
        }
        let count = 0;
        for (let i = 0; i < formatted.length; i++) {
            if (unformatted[count] === formatted[i]) {
                if (count === index) {
                    return i;
                }
                count++;
            }
        }
        return formatted.length;
    }

    /**
     * Finds all matches for a regex in text with infinite loop protection.
     * @param regex - The regular expression to execute
     * @param text - The text to search in
     * @returns Array of match results with index, length, and match string
     */
    private findAllMatches(regex: RegExp, text: string): { index: number; length: number; match: string }[] {
        const matches: { index: number; length: number; match: string }[] = [];
        let match: RegExpExecArray | null;
        let lastIndex = 0;
        
        while ((match = regex.exec(text)) !== null) {
            if (match.index === lastIndex && match[0].length === 0) {
                break;
            }

            matches.push({
                index: match.index,
                length: match[0].length,
                match: match[0]
            });

            lastIndex = match.index + match[0].length;

            if (regex.lastIndex <= match.index) {
                regex.lastIndex = match.index + 1;
            }
        }
        
        return matches;
    }

    /**
     * Merges overlapping ranges into consolidated ranges.
     * @param ranges - Array of ranges with start and end positions
     * @param text - Optional text for term reconstruction
     * @returns Array of merged ranges
     */
    private mergeOverlappingRanges<T extends { start: number; end: number }>(
        ranges: T[], 
        text?: string
    ): T[] {
        if (ranges.length === 0) return [];

        const merged: T[] = [];
        
        for (const range of ranges) {
            if (merged.length === 0) {
                merged.push(range);
            } else {
                const last = merged[merged.length - 1];
                if (last.end >= range.start) {
                    last.end = Math.max(last.end, range.end);
                    if (text && 'term' in last) {
                        (last as any).term = text.slice(last.start, last.end);
                    }
                } else {
                    merged.push(range);
                }
            }
        }
        
        return merged;
    }

    /**
     * Escapes special regex characters in the search term.
     * @param text - Text to escape
     * @returns Escaped text safe for regex
     */
    private escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
